import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Send, Flag, Ban, UserX, ArrowLeft, MessageCircle } from 'lucide-react';
import { SCShell, SCHeading, ghostBtn, RequireAuth } from './sc/SCShared';
import { scConversations, scMessages, scSend, scReport, scBlock, scUnmatch, scFileUrl } from '../api';

const CATS = ['Harassment', 'Spam', 'Scam', 'Impersonation', 'Threats', 'Sexual harassment', 'Hate/abusive behavior', 'Underage concern', 'Financial solicitation', 'Fake profile', 'Other'];

const Inner = () => {
  const [params, setParams] = useSearchParams();
  const active = params.get('match');
  const [convos, setConvos] = useState([]);
  const [thread, setThread] = useState(null);
  const [other, setOther] = useState(null);
  const [text, setText] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const [cat, setCat] = useState('Harassment');
  const [desc, setDesc] = useState('');
  const endRef = useRef();

  const loadConvos = () => scConversations().then((d) => setConvos(d.conversations || [])).catch(() => {});
  const loadThread = (mid) => scMessages(mid).then((d) => { setThread(d.messages || []); setOther(d.other); setTimeout(() => endRef.current?.scrollIntoView(), 50); }).catch((e) => toast.error(e?.response?.data?.detail || 'Could not open conversation'));

  useEffect(() => { loadConvos(); }, []);
  useEffect(() => { if (active) loadThread(active); else { setThread(null); setOther(null); } /* eslint-disable-next-line */ }, [active]);

  const send = async () => {
    const t = text.trim(); if (!t || !active) return;
    setText('');
    try { const m = await scSend(active, t); setThread((p) => [...(p || []), m]); setTimeout(() => endRef.current?.scrollIntoView(), 50); loadConvos(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not send'); }
  };
  const submitReport = async () => {
    if (!other) return;
    try { await scReport({ reported_user_id: other.user_id, match_id: active, category: cat, description: desc }); toast.success('Report submitted. Thank you — our team will review it.'); setReportOpen(false); setDesc(''); }
    catch { toast.error('Could not submit report'); }
  };
  const doBlock = async () => { if (!other) return; try { await scBlock(other.user_id); toast.success('User blocked.'); setParams({}); loadConvos(); } catch { toast.error('Could not block'); } };
  const doUnmatch = async () => { if (!active) return; try { await scUnmatch(active); toast.success('Unmatched.'); setParams({}); loadConvos(); } catch { toast.error('Could not unmatch'); } };

  return (
    <SCShell>
      <SCHeading eyebrow="Messages" title="Your conversations" sub="Private 1:1 messaging — only with people you've matched with." />
      <div className="grid md:grid-cols-3 gap-6">
        <div className={`${active ? 'hidden md:block' : ''} rounded-2xl border border-[#8b5cf6]/25 bg-white/[0.03] backdrop-blur-md overflow-hidden`} data-testid="sc-convo-list">
          {convos.length === 0 ? <p className="p-6 font-jost text-sm text-[#e9d8ff]/50">No conversations yet. Match with someone to start chatting.</p>
            : convos.map((c) => (
              <button key={c.match_id} onClick={() => setParams({ match: c.match_id })} className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-white/5 hover:bg-white/5 ${active === c.match_id ? 'bg-[#8b5cf6]/10' : ''}`} data-testid={`sc-convo-${c.match_id}`}>
                {c.profile.profile_photo ? <img src={scFileUrl(c.profile.profile_photo)} alt="" className="h-10 w-10 rounded-full object-cover" /> : <span className="h-10 w-10 rounded-full bg-[#8b5cf6]/20 flex items-center justify-center font-cinzel text-[#e9d8ff]">{(c.profile.display_name || '?')[0]}</span>}
                <span className="flex-1 min-w-0"><span className="block font-jost text-sm text-[#f5d786] truncate">{c.profile.display_name}</span><span className="block font-jost text-xs text-[#e9d8ff]/50 truncate">{c.last_message || 'Say hello ✨'}</span></span>
                {c.unread > 0 && <span className="text-[10px] bg-[#d946ef] text-white rounded-full px-2 py-0.5">{c.unread}</span>}
              </button>
            ))}
        </div>

        <div className={`${active ? '' : 'hidden md:flex'} md:col-span-2 rounded-2xl border border-[#8b5cf6]/25 bg-white/[0.03] backdrop-blur-md flex flex-col min-h-[460px]`}>
          {!active || !other ? <div className="flex-1 flex items-center justify-center text-[#e9d8ff]/40 font-cormorant text-xl"><MessageCircle className="h-5 w-5 mr-2" /> Select a conversation</div> : (<>
            <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/10">
              <div className="flex items-center gap-3">
                <button className="md:hidden text-[#e9d8ff]" onClick={() => setParams({})}><ArrowLeft className="h-5 w-5" /></button>
                <span className="font-cinzel text-[#f5d786]">{other.display_name}{other.age ? `, ${other.age}` : ''}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setReportOpen((v) => !v)} title="Report" className="text-[#e9d8ff]/70 hover:text-[#ff6b9d]" data-testid="sc-report-btn"><Flag className="h-4 w-4" /></button>
                <button onClick={doBlock} title="Block" className="text-[#e9d8ff]/70 hover:text-[#ff6b9d]" data-testid="sc-block-btn"><Ban className="h-4 w-4" /></button>
                <button onClick={doUnmatch} title="Unmatch" className="text-[#e9d8ff]/70 hover:text-[#ff6b9d]" data-testid="sc-unmatch-btn"><UserX className="h-4 w-4" /></button>
              </div>
            </div>
            {reportOpen && (
              <div className="px-4 py-3 border-b border-white/10 bg-black/20 space-y-2" data-testid="sc-report-panel">
                <select value={cat} onChange={(e) => setCat(e.target.value)} className="w-full bg-black/40 border border-[#8b5cf6]/30 rounded-lg px-3 py-2 text-sm text-[#e9d8ff]">{CATS.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} rows={2} placeholder="Describe the issue (optional)" className="w-full bg-black/40 border border-[#8b5cf6]/30 rounded-lg px-3 py-2 text-sm text-[#e9d8ff]" />
                <button onClick={submitReport} className="text-xs bg-[#ff6b9d] text-black rounded-full px-4 py-2" data-testid="sc-report-submit">Submit report</button>
              </div>
            )}
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="sc-thread">
              {(thread || []).map((m) => (
                <div key={m.id} className={`flex ${m.mine ? 'justify-end' : 'justify-start'}`}>
                  <span className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm font-jost ${m.mine ? 'bg-gradient-to-r from-[#7c3aed] to-[#d946ef] text-white' : 'bg-white/10 text-[#e9d8ff]'}`}>{m.message}</span>
                </div>
              ))}
              <div ref={endRef} />
            </div>
            <div className="p-3 border-t border-white/10 flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Write a message…" className="flex-1 bg-black/30 border border-[#8b5cf6]/30 rounded-full px-4 py-2.5 text-sm text-[#e9d8ff] focus:outline-none focus:border-[#d946ef]" data-testid="sc-msg-input" />
              <button onClick={send} className="rounded-full px-4 bg-gradient-to-r from-[#7c3aed] to-[#d946ef] text-white" data-testid="sc-msg-send"><Send className="h-4 w-4" /></button>
            </div>
          </>)}
        </div>
      </div>
    </SCShell>
  );
};

const SCMessages = () => <RequireAuth><Inner /></RequireAuth>;
export default SCMessages;
